import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShelfFeed, useIsMobile } from '../../hooks/useReaderCorner';

const C = { rust: '#B85C38', forest: '#2D4A3E', cream: '#F5F0E8', gold: '#d4a84b', grey: '#8B7355', ink: '#292524' };

const EVENT_META = {
  to_know_me:         { label: 'To Know Me',         color: 'var(--brand-gold)', action: 'added' },
  recommend_me:        { label: 'Recommend Me',        color: 'var(--brand-rust)', action: 'is recommending' },
  custom_shelf:        { label: 'Custom Shelf',        color: '#7c3aed', action: 'shelved' },
  currently_reading:   { label: 'Currently Reading',   color: 'var(--brand-forest)', action: 'started' },
  top_trope:           { label: 'Top Tropes',          color: '#be185d', action: 'added trope' },
};

function timeAgo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function Avatar({ username, avatar_url, size = 28 }) {
  const navigate = useNavigate();
  const initial = (username || '?').slice(0, 1).toUpperCase();
  return (
    <button
      onClick={() => username && navigate(`/u/${username}`)}
      style={{
        width: size, height: size, borderRadius: 'var(--radius-circle)', flexShrink: 0,
        background: avatar_url ? `url(${avatar_url}) center/cover` : C.rust,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.42, fontWeight: 700, color: 'var(--brand-white)', fontFamily: 'var(--font-display)',
        border: `2px solid ${C.gold}`, cursor: username ? 'pointer' : 'default', padding: 'var(--space-none)',
      }}
    >
      {!avatar_url && initial}
    </button>
  );
}

function Handle({ username }) {
  const navigate = useNavigate();
  if (!username) return null;
  return (
    <button onClick={() => navigate(`/u/${username}`)}
      style={{ background: 'none', border: 'none', padding: 'var(--space-none)', fontSize: 12.5, color: C.forest, fontWeight: 700, cursor: 'pointer' }}>
      @{username}
    </button>
  );
}

function displayItem(e) {
  const meta = EVENT_META[e.event_type];
  if (e.event_type === 'top_trope') return { label: meta.label, item: e.trope_name, why: null };
  if (e.event_type === 'custom_shelf') return { label: `Shelf · ${e.shelf_name || 'a shelf'}`, item: e.book_title, why: null };
  return { label: meta.label, item: e.book_title, why: null };
}

function ShelfRow({ e, onOpenProfile }) {
  const meta = EVENT_META[e.event_type];
  const { label, item } = displayItem(e);
  return (
    <div style={{
      background: '#fffdf8', border: `1px solid ${C.gold}66`, borderRadius: 'var(--radius-pill)',
      boxShadow: '0 5px 20px rgba(45,74,62,0.07)', display: 'flex', alignItems: 'center',
      gap: 'var(--space-md)', padding: '13px 18px',
    }}>
      <Avatar username={e.username} avatar_url={e.avatar_url} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-sm)' }}>
          <p style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: meta.color, margin: 'var(--space-none)', whiteSpace: 'nowrap' }}>{label}</p>
          <span style={{ fontSize: 9.5, color: '#b3a88f' }}>◷ {timeAgo(e.created_at)}</span>
        </div>
        <p style={{ fontSize: 12.5, color: C.ink, margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Handle username={e.username} />{' '}
          <span style={{ color: C.grey, fontStyle: 'italic' }}>{meta.action}</span>{' '}
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: C.forest }}>{item}</span>
        </p>
      </div>
      <button onClick={() => e.username && onOpenProfile(e.username)}
        style={{ padding: '6px 14px', background: 'transparent', border: `1px solid ${C.forest}`, borderRadius: 'var(--radius-pill)', fontSize: 10.5, fontWeight: 700, color: C.forest, cursor: 'pointer', flexShrink: 0 }}>
        See shelf →
      </button>
    </div>
  );
}

export default function ShelvesTab() {
  const [filter, setFilter] = useState('all');
  const [shown, setShown]   = useState(14);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { items, loading } = useShelfFeed(filter);
  const visible = items.slice(0, shown);

  function chip(active) {
    return {
      padding: isMobile ? '6px 12px' : '7px 15px', borderRadius: 'var(--radius-pill)', fontSize: isMobile ? 10.5 : 11.5, fontWeight: 700, letterSpacing: 0.5, cursor: 'pointer',
      background: active ? C.gold : 'transparent', color: C.forest,
      border: active ? `1px solid ${C.gold}` : `1px solid ${C.forest}44`,
      whiteSpace: 'nowrap', flexShrink: 0,
    };
  }

  return (
    <div>
      <div className="rc-hide-scrollbar" style={{
        display: 'flex', gap: isMobile ? 7 : 9, justifyContent: isMobile ? 'flex-start' : 'center',
        marginBottom: 'var(--space-xxl)', flexWrap: isMobile ? 'nowrap' : 'wrap',
        overflowX: isMobile ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch',
        padding: isMobile ? '0 2px 4px' : 0,
      }}>
        <button onClick={() => { setFilter('all'); setShown(14); }} style={chip(filter === 'all')}>All{loading ? '' : ` · ${items.length}`}</button>
        {Object.entries(EVENT_META).map(([key, meta]) => (
          <button key={key} onClick={() => { setFilter(key); setShown(14); }} style={chip(filter === key)}>{meta.label}</button>
        ))}
      </div>

      {loading && items.length === 0 && <p style={{ textAlign: 'center', color: C.grey, fontSize: 'var(--font-size-base)' }}>Loading…</p>}
      {!loading && items.length === 0 && (
        <p style={{ textAlign: 'center', color: C.grey, fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>No shelf activity yet.</p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 10 : 14, alignItems: 'start' }}>
        {visible.map(e => <ShelfRow key={e.id} e={e} onOpenProfile={u => navigate(`/u/${u}`)} />)}
      </div>

      {shown < items.length && (
        <div style={{ textAlign: 'center', marginTop: 26 }}>
          <button onClick={() => setShown(n => n + 14)}
            style={{ padding: '11px 34px', background: 'transparent', border: `1.5px solid ${C.forest}`, borderRadius: 'var(--radius-pill)', fontSize: 12.5, fontWeight: 700, letterSpacing: 1, color: C.forest, cursor: 'pointer' }}>
            Load more · {items.length - shown} left
          </button>
        </div>
      )}
    </div>
  );
}